import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchGogglesCrashVariants, fetchVariantVersions, fetchVersionCrashes, fetchCrashDetails } from '../services/gogglesCrashesService';
import { getCrashDetails as getUserCrashDetails, desymbolicate, fetchUserProfiles } from '../services/usersService';
import { getHardwareModel } from '../utils/hardware';
import { useError } from '../contexts/ErrorContext';
import './GogglesCrashesReport.css';

/** Human-readable label for a numeric-string hardware variant, e.g. "4" -> "2026 MY REKKIE Goggles (HW 4)". */
const variantLabel = (variant) => {
  const model = getHardwareModel(variant) || 'UNKNOWN';
  return `${model} (HW ${variant})`;
};

/**
 * Parses a storage path like "goggles_crashes/{uid}/{day}/{filename}"
 * into its components for use with the crash-details and desymbolicate endpoints.
 */
const parseStoragePath = (storagePath) => {
  if (!storagePath) return null;
  const parts = storagePath.split('/');
  if (parts.length < 4 || parts[0] !== 'goggles_crashes') return null;
  return {
    uid: parts[1],
    day: parts[2],
    filename: parts.slice(3).join('/')
  };
};

const GogglesCrashesReport = () => {
  const { addError } = useError();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read navigation state from URL (variant -> version -> crash)
  const selectedVariant = searchParams.get('variant') || null;
  const selectedVersion = searchParams.get('version') || null;
  const selectedCrash = searchParams.get('crash') || null;

  // Helper to update search params while preserving the report param
  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value == null) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      return next;
    });
  }, [setSearchParams]);

  // Map of user IDs to names
  const [userNameMap, setUserNameMap] = useState({});

  // State for variants list
  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);

  // State for versions list (for the selected variant)
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // State for selected version's crashes
  const [versionCrashes, setVersionCrashes] = useState([]);
  const [crashesLoading, setCrashesLoading] = useState(false);

  // State for selected crash details
  const [crashDetails, setCrashDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // State for occurrence crash detail modal
  const [selectedOccurrence, setSelectedOccurrence] = useState(null);
  const [occurrenceDetails, setOccurrenceDetails] = useState(null);
  const [occurrenceLoading, setOccurrenceLoading] = useState(false);
  const [desymbolicateResult, setDesymbolicateResult] = useState(null);
  const [desymbolicateLoading, setDesymbolicateLoading] = useState(false);

  // Fetch all variants on mount
  const loadVariants = useCallback(async () => {
    setVariantsLoading(true);
    try {
      const data = await fetchGogglesCrashVariants(addError);
      setVariants(data.variants || []);
    } catch (error) {
      console.error('Failed to load variants:', error);
    } finally {
      setVariantsLoading(false);
    }
  }, [addError]);

  useEffect(() => {
    loadVariants();
  }, [loadVariants]);

  // Label crash occurrences with user names, looked up for the uids on screen
  useEffect(() => {
    const uids = [...new Set((crashDetails?.crashes || []).map(c => c.user_id).filter(Boolean))]
      .filter(uid => !(uid in userNameMap));
    if (uids.length === 0) return;
    fetchUserProfiles(uids).then(profiles => {
      setUserNameMap(prev => {
        const next = { ...prev };
        uids.forEach(uid => { next[uid] = uid; });
        profiles.forEach(p => { next[p.uid] = p.name || p.email || p.uid; });
        return next;
      });
    });
    // userNameMap is read only to skip uids already looked up
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crashDetails]);

  // Load versions when a variant is selected (including on initial load from URL)
  const loadVersions = useCallback(() => {
    if (!selectedVariant) { setVersions([]); return; }
    setVersionsLoading(true);
    fetchVariantVersions(selectedVariant, addError)
      .then((data) => setVersions(data.versions || []))
      .catch(() => setVersions([]))
      .finally(() => setVersionsLoading(false));
  }, [selectedVariant, addError]);

  useEffect(() => { loadVersions(); }, [loadVersions]);

  // Load version crashes when variant + version are set (including on initial load from URL)
  useEffect(() => {
    if (selectedVariant && selectedVersion) {
      setCrashesLoading(true);
      fetchVersionCrashes(selectedVariant, selectedVersion, addError)
        .then((data) => setVersionCrashes(data.crashes || []))
        .catch(() => setVersionCrashes([]))
        .finally(() => setCrashesLoading(false));
    } else {
      setVersionCrashes([]);
    }
  }, [selectedVariant, selectedVersion, addError]);

  // Load crash details when variant + version + crash are set (including on initial load from URL)
  useEffect(() => {
    if (selectedVariant && selectedVersion && selectedCrash) {
      setDetailsLoading(true);
      fetchCrashDetails(selectedVariant, selectedVersion, selectedCrash, addError)
        .then((data) => setCrashDetails(data.data || null))
        .catch(() => setCrashDetails(null))
        .finally(() => setDetailsLoading(false));
    } else {
      setCrashDetails(null);
    }
  }, [selectedVariant, selectedVersion, selectedCrash, addError]);

  // Navigate to a variant
  const handleSelectVariant = (variant) => {
    updateParams({ variant, version: null, crash: null });
  };

  // Navigate to a version
  const handleSelectVersion = (version) => {
    updateParams({ version, crash: null });
  };

  // Navigate to a crash
  const handleSelectCrash = (crashSha) => {
    updateParams({ crash: crashSha });
  };

  // Go back to variants list
  const handleBackToVariants = () => {
    updateParams({ variant: null, version: null, crash: null });
  };

  // Go back to versions list
  const handleBackToVersions = () => {
    updateParams({ version: null, crash: null });
  };

  // Go back to crashes list
  const handleBackToCrashes = () => {
    updateParams({ crash: null });
  };

  // Open occurrence detail modal
  const handleOccurrenceClick = useCallback(async (occurrence) => {
    const parsed = parseStoragePath(occurrence.path);
    if (!parsed) {
      addError('Could not parse storage path: ' + occurrence.path, null, 'Goggles Crashes');
      return;
    }

    setSelectedOccurrence({ ...occurrence, ...parsed });
    setOccurrenceDetails(null);
    setDesymbolicateResult(null);
    setOccurrenceLoading(true);

    try {
      const details = await getUserCrashDetails(parsed.uid, parsed.day, parsed.filename, addError);
      setOccurrenceDetails(details);
    } catch (error) {
      console.error('Failed to load occurrence crash details:', error);
    } finally {
      setOccurrenceLoading(false);
    }
  }, [addError]);

  const handleCloseOccurrence = () => {
    setSelectedOccurrence(null);
    setOccurrenceDetails(null);
    setDesymbolicateResult(null);
  };

  const handleDesymbolicate = useCallback(async () => {
    if (!selectedOccurrence || desymbolicateLoading) return;

    setDesymbolicateLoading(true);
    setDesymbolicateResult(null);

    try {
      const result = await desymbolicate(
        selectedOccurrence.uid,
        selectedOccurrence.day,
        selectedOccurrence.filename,
        addError
      );
      setDesymbolicateResult(result);
    } catch (err) {
      setDesymbolicateResult({ error: err.message });
    } finally {
      setDesymbolicateLoading(false);
    }
  }, [selectedOccurrence, desymbolicateLoading, addError]);

  // Format timestamp
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render variants list view (top level)
  const renderVariantsList = () => (
    <div className="goggles-crashes-section">
      <div className="goggles-crashes-section-header">
        <h2 className="goggles-crashes-section-title">Hardware Variants</h2>
        <button
          className="goggles-crashes-refresh-btn"
          onClick={loadVariants}
          disabled={variantsLoading}
        >
          {variantsLoading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {variantsLoading ? (
        <div className="goggles-crashes-loading">Loading variants...</div>
      ) : variants.length === 0 ? (
        <div className="goggles-crashes-empty">No variants found</div>
      ) : (
        <div className="goggles-crashes-grid">
          {variants.map((variant) => (
            <div
              key={variant}
              className="goggles-crashes-version-card"
              onClick={() => handleSelectVariant(variant)}
            >
              <div className="goggles-crashes-version-name">{getHardwareModel(variant) || 'UNKNOWN'}</div>
              <div className="goggles-crashes-version-action">HW {variant} • View Versions →</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render versions list view (for the selected variant)
  const renderVersionsList = () => (
    <div className="goggles-crashes-section">
      <div className="goggles-crashes-section-header">
        <div className="goggles-crashes-breadcrumb">
          <button
            className="goggles-crashes-back-btn"
            onClick={handleBackToVariants}
          >
            ← Back
          </button>
          <h2 className="goggles-crashes-section-title">
            Software Versions — {variantLabel(selectedVariant)}
          </h2>
        </div>
        <button
          className="goggles-crashes-refresh-btn"
          onClick={loadVersions}
          disabled={versionsLoading}
        >
          {versionsLoading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {versionsLoading ? (
        <div className="goggles-crashes-loading">Loading versions...</div>
      ) : versions.length === 0 ? (
        <div className="goggles-crashes-empty">No versions found</div>
      ) : (
        <div className="goggles-crashes-grid">
          {versions.map((version) => (
            <div
              key={version}
              className="goggles-crashes-version-card"
              onClick={() => handleSelectVersion(version)}
            >
              <div className="goggles-crashes-version-name">{version}</div>
              <div className="goggles-crashes-version-action">View Crashes →</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render crashes list for selected version
  const renderCrashesList = () => (
    <div className="goggles-crashes-section">
      <div className="goggles-crashes-section-header">
        <div className="goggles-crashes-breadcrumb">
          <button 
            className="goggles-crashes-back-btn"
            onClick={handleBackToVersions}
          >
            ← Back
          </button>
          <h2 className="goggles-crashes-section-title">
            Crashes for {variantLabel(selectedVariant)} — Version {selectedVersion}
          </h2>
        </div>
        <button 
          className="goggles-crashes-refresh-btn"
          onClick={() => handleSelectVersion(selectedVersion)}
          disabled={crashesLoading}
        >
          {crashesLoading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {crashesLoading ? (
        <div className="goggles-crashes-loading">Loading crashes...</div>
      ) : versionCrashes.length === 0 ? (
        <div className="goggles-crashes-empty">No crashes found for this version</div>
      ) : (
        <>
          <div className="goggles-crashes-summary">
            Total unique crashes: {versionCrashes.length}
          </div>
          <div className="goggles-crashes-table-wrapper">
            <table className="goggles-crashes-table">
              <thead>
                <tr>
                  <th>Crash SHA</th>
                  <th>Task Name</th>
                  <th>Count</th>
                  <th>First Seen</th>
                  <th>Last Seen</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {[...versionCrashes].sort((a, b) => b.count - a.count).map((crash) => {
                  const crashUrl = `/analytics?report=goggles-crashes&variant=${encodeURIComponent(selectedVariant)}&version=${encodeURIComponent(selectedVersion)}&crash=${encodeURIComponent(crash.crashSha)}`;
                  return (
                    <tr key={crash.crashSha}>
                      <td className="goggles-crashes-sha">{crash.crashSha.substring(0, 16)}...</td>
                      <td>{crash.name_of_task || 'N/A'}</td>
                      <td className="goggles-crashes-count">{crash.count}</td>
                      <td>{formatDate(crash.first_seen)}</td>
                      <td>{formatDate(crash.last_seen)}</td>
                      <td>
                        <a
                          href={crashUrl}
                          className="goggles-crashes-view-btn"
                          onClick={(e) => {
                            if (!e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
                              e.preventDefault();
                              handleSelectCrash(crash.crashSha);
                            }
                          }}
                        >
                          View Details
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  // Render crash details view
  const renderCrashDetails = () => (
    <div className="goggles-crashes-section">
      <div className="goggles-crashes-section-header">
        <div className="goggles-crashes-breadcrumb">
          <button 
            className="goggles-crashes-back-btn"
            onClick={handleBackToCrashes}
          >
            ← Back to Crashes
          </button>
          <h2 className="goggles-crashes-section-title">
            Crash Details
          </h2>
        </div>
      </div>

      {detailsLoading ? (
        <div className="goggles-crashes-loading">Loading crash details...</div>
      ) : !crashDetails ? (
        <div className="goggles-crashes-empty">No crash details available</div>
      ) : (
        <div className="goggles-crashes-details">
          <div className="goggles-crashes-detail-section">
            <h3 className="goggles-crashes-detail-heading">Summary</h3>
            <div className="goggles-crashes-detail-grid">
              <div className="goggles-crashes-detail-item">
                <span className="goggles-crashes-detail-label">Crash SHA:</span>
                <span className="goggles-crashes-detail-value goggles-crashes-sha-full">
                  {selectedCrash}
                </span>
              </div>
              <div className="goggles-crashes-detail-item">
                <span className="goggles-crashes-detail-label">Hardware:</span>
                <span className="goggles-crashes-detail-value">{variantLabel(selectedVariant)}</span>
              </div>
              <div className="goggles-crashes-detail-item">
                <span className="goggles-crashes-detail-label">Version:</span>
                <span className="goggles-crashes-detail-value">{selectedVersion}</span>
              </div>
              <div className="goggles-crashes-detail-item">
                <span className="goggles-crashes-detail-label">Task Name:</span>
                <span className="goggles-crashes-detail-value">{crashDetails.name_of_task || 'N/A'}</span>
              </div>
              <div className="goggles-crashes-detail-item">
                <span className="goggles-crashes-detail-label">Total Count:</span>
                <span className="goggles-crashes-detail-value">{crashDetails.count}</span>
              </div>
              <div className="goggles-crashes-detail-item">
                <span className="goggles-crashes-detail-label">First Seen:</span>
                <span className="goggles-crashes-detail-value">{formatDate(crashDetails.first_seen)}</span>
              </div>
              <div className="goggles-crashes-detail-item">
                <span className="goggles-crashes-detail-label">Last Seen:</span>
                <span className="goggles-crashes-detail-value">{formatDate(crashDetails.last_seen)}</span>
              </div>
            </div>
          </div>

          {crashDetails.sample_backtrace_addresses && crashDetails.sample_backtrace_addresses.length > 0 && (
            <div className="goggles-crashes-detail-section">
              <h3 className="goggles-crashes-detail-heading">Backtrace Addresses</h3>
              <div className="goggles-crashes-backtrace">
                <code className="goggles-crashes-code">
                  {crashDetails.sample_backtrace_addresses.map((addr, idx) => (
                    <div key={idx}>
                      {idx}: 0x{addr.toString(16).padStart(8, '0')}
                    </div>
                  ))}
                </code>
              </div>
            </div>
          )}

          {crashDetails.crashes && crashDetails.crashes.length > 0 && (
            <div className="goggles-crashes-detail-section">
              <h3 className="goggles-crashes-detail-heading">
                Crash Occurrences ({crashDetails.crashes.length})
              </h3>
              <div className="goggles-crashes-table-wrapper">
                <table className="goggles-crashes-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Timestamp</th>
                      <th>Storage Path</th>
                    </tr>
                  </thead>
                  <tbody>
                    {crashDetails.crashes.map((occurrence, idx) => (
                      <tr
                        key={idx}
                        className="goggles-crashes-occurrence-row"
                        onClick={() => handleOccurrenceClick(occurrence)}
                      >
                        <td className="goggles-crashes-user-id">{userNameMap[occurrence.user_id] || occurrence.user_id}</td>
                        <td>{formatDate(new Date(occurrence.timestamp * 1000).toISOString())}</td>
                        <td className="goggles-crashes-path">{occurrence.path}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderOccurrenceModal = () => {
    if (!selectedOccurrence) return null;

    return (
      <div className="goggles-crash-overlay" onClick={handleCloseOccurrence}>
        <div className="goggles-crash-panel" onClick={(e) => e.stopPropagation()}>
          <div className="goggles-crash-panel-header">
            <h3 className="goggles-crash-panel-title">Crash Report</h3>
            <button className="goggles-crash-panel-close" onClick={handleCloseOccurrence}>
              &times;
            </button>
          </div>

          <div className="goggles-crash-panel-meta">
            <span className="goggles-crash-panel-meta-item">
              User: {selectedOccurrence.user_id}
            </span>
            <span className="goggles-crash-panel-meta-item goggles-crash-panel-version">
              {selectedVersion}
            </span>
          </div>

          <div className="goggles-crash-panel-content">
            {occurrenceLoading && (
              <div className="goggles-crash-panel-loading">Loading crash details...</div>
            )}

            {!occurrenceLoading && !occurrenceDetails && (
              <div className="goggles-crash-panel-error">Failed to load crash details</div>
            )}

            {!occurrenceLoading && occurrenceDetails && (
              <>
                <div className="goggles-crash-section">
                  <h4 className="goggles-crash-label">Task Name</h4>
                  <div className="goggles-crash-value goggles-crash-mono">
                    {occurrenceDetails.nameOfTask}
                  </div>
                </div>

                <div className="goggles-crash-section">
                  <h4 className="goggles-crash-label">Core Dump Version</h4>
                  <div className="goggles-crash-value">
                    {occurrenceDetails.coreDumpVersion}
                  </div>
                </div>

                {occurrenceDetails.extraInfo && (
                  <div className="goggles-crash-section">
                    <h4 className="goggles-crash-label">Exception Info</h4>
                    <div className="goggles-crash-info-grid">
                      <div className="goggles-crash-info-item">
                        <span className="goggles-crash-info-label">Cause</span>
                        <span className="goggles-crash-info-value">
                          {occurrenceDetails.extraInfo.cause}
                        </span>
                      </div>
                      {occurrenceDetails.extraInfo.virtualAddress && (
                        <div className="goggles-crash-info-item">
                          <span className="goggles-crash-info-label">Virtual Address</span>
                          <span className="goggles-crash-info-value goggles-crash-mono">
                            {occurrenceDetails.extraInfo.virtualAddress}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {occurrenceDetails.backtrace && (
                  <div className="goggles-crash-section">
                    <h4 className="goggles-crash-label">
                      Backtrace
                      {occurrenceDetails.backtrace.corrupted && (
                        <span className="goggles-crash-corrupted">CORRUPTED</span>
                      )}
                    </h4>
                    <div className="goggles-crash-value">
                      <span className="goggles-crash-depth">
                        Depth: {occurrenceDetails.backtrace.depth}
                      </span>
                    </div>
                    {occurrenceDetails.backtrace.addresses?.length > 0 && (
                      <div className="goggles-crash-addresses">
                        {occurrenceDetails.backtrace.addresses.map((addr, index) => (
                          <div key={index} className="goggles-crash-addr-row">
                            <span className="goggles-crash-addr-index">{index}</span>
                            <span className="goggles-crash-addr-value">{addr}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {occurrenceDetails.appElfSha256 && (
                  <div className="goggles-crash-section">
                    <h4 className="goggles-crash-label">App ELF SHA256</h4>
                    <div className="goggles-crash-value goggles-crash-mono goggles-crash-sha">
                      {occurrenceDetails.appElfSha256.length > 40
                        ? occurrenceDetails.appElfSha256
                        : occurrenceDetails.appElfSha256.toUpperCase().match(/.{1,8}/g)?.join(' ') || occurrenceDetails.appElfSha256}
                    </div>
                  </div>
                )}

                {occurrenceDetails.fullText && (
                  <div className="goggles-crash-section">
                    <h4 className="goggles-crash-label">Full Proto Message</h4>
                    <pre className="goggles-crash-proto">{occurrenceDetails.fullText}</pre>
                  </div>
                )}

                <div className="goggles-crash-section goggles-crash-desym-section">
                  <div className="goggles-crash-desym-header">
                    <h4 className="goggles-crash-label">Desymbolicate Backtrace</h4>
                    <button
                      className={`goggles-crash-desym-btn ${desymbolicateLoading ? 'loading' : ''}`}
                      onClick={handleDesymbolicate}
                      disabled={desymbolicateLoading || !occurrenceDetails.backtrace?.addresses?.length}
                    >
                      {desymbolicateLoading ? 'Desymbolicating...' : 'Desymbolicate'}
                    </button>
                  </div>

                  {desymbolicateResult && (
                    <div className="goggles-crash-desym-result">
                      {desymbolicateResult.error ? (
                        <div className="goggles-crash-desym-error">{desymbolicateResult.error}</div>
                      ) : (
                        <>
                          <div className="goggles-crash-desym-info">
                            <span className="goggles-crash-desym-elf">
                              ELF: {desymbolicateResult.elfStoragePath}
                            </span>
                          </div>
                          <div className="goggles-crash-desym-list">
                            {desymbolicateResult.symbolicated?.map((item, index) => (
                              <div key={index} className="goggles-crash-desym-item">
                                <span className="goggles-crash-desym-address">{item.address}</span>
                                <span className="goggles-crash-desym-symbol">{item.symbol}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="goggles-crashes-report">
      <div className="goggles-crashes-header">
        <div className="goggles-crashes-title-section">
          <h1 className="goggles-crashes-title">Goggles Crashes</h1>
          <p className="goggles-crashes-description">
            View and analyze unique crashes across all software versions
          </p>
        </div>
      </div>

      <div className="goggles-crashes-content">
        {selectedCrash
          ? renderCrashDetails()
          : selectedVersion
          ? renderCrashesList()
          : selectedVariant
          ? renderVersionsList()
          : renderVariantsList()}
      </div>

      {renderOccurrenceModal()}
    </div>
  );
};

export default GogglesCrashesReport;
